import { Tabs, usePathname } from 'expo-router'
import { Home, Swords, Users } from 'lucide-react-native'
import { View } from 'react-native'
import { useMemo } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { AuthGuard } from '../../components/layout/AuthGuard'
import { FloatingCreateButton } from '../../components/layout/FloatingCreateButton'
import { WarmupFab } from '../../components/worldcup/WarmupFab/WarmupFab'
import { WorldCupTabIcon } from '../../components/layout/WorldCupTabIcon'
import {
  getLayoutStyles,
} from '../../appStyles/_layout.styles'
import {
  HIDE_TAB_BAR_ROUTES,
  WORLDCUP_MATCH_ROUTE_PREFIX,
  LEAGUE_DETAIL_ROUTE,
  MATCH_ROUTE_PREFIX,
  INDEX_ROUTE_KEY,
  CREATE_ROUTE_KEY,
} from '../../appStyles/_layout.constants'
import { useTheme } from '../../src/theme/useTheme'
import { useReceivedInvitesCount } from '../../src/friends/hooks/useReceivedInvitesCount'
import { ZportiBadge } from '../../components/ui/zportiBadge'
import { ScreenTracker } from '../../src/analytics/ScreenTracker'
import { useDeepLinkHandler } from '../../src/shared/hooks/useDeepLinkHandler'
import { usePendingB2bJoinHandler } from '../../src/join/hooks/usePendingB2bJoinHandler'
import { usePendingWcSquadJoinHandler } from '../../src/join/hooks/usePendingWcSquadJoinHandler'
import { useGuestGuard } from '../../src/shared/hooks/useGuestGuard'
import { useWorldCupWelcomeMenuStore } from '../../src/worldcup/stores/worldCupWelcomeMenuStore'
import { useSquadTvModeStore } from '../../src/worldcup/stores/squadTvModeStore'
import { getWcBrand } from '../../src/worldcup/helpers/wcBrand.helper'

import { soundService } from '../../src/shared/services/SoundService'
import { useSoundStore } from '../../src/shared/stores/soundStore'
import { SOUND_IDS } from '../../src/shared/constants/sounds.constants'
import { PendingGuestMonkeyChallengeFlush } from '../../src/create/components/PendingGuestMonkeyChallengeFlush'
import { GuestWalkthroughOverlay } from '../../components/walkthrough/GuestWalkthroughOverlay'
import { FirstChallengeTourOverlay } from '../../components/walkthrough/FirstChallengeTourOverlay/FirstChallengeTourOverlay'

function AuthenticatedTabs() {
  const theme = useTheme()
  const pathname = usePathname()
  const insets = useSafeAreaInsets()
  const worldCupWelcomeOverlayVisible = useWorldCupWelcomeMenuStore((s) => s.welcomeOverlayVisible)
  const isSquadTvModeActive = useSquadTvModeStore((s) => s.isActive)

  // Process any pending deep link after sign-in (hydration-aware)
  useDeepLinkHandler()
  // Complete pending B2B org join when post-auth routing skipped /join?join=CODE
  usePendingB2bJoinHandler()
  // Complete pending WC squad join when the sessionStorage-only returnUrl was lost
  // across OAuth (e.g. invite link opened in an in-app browser like WhatsApp)
  usePendingWcSquadJoinHandler()

  // Note: the Arena/create walkthrough used to auto-fire on first sign-in. It is
  // now triggered by the "World Cup → Arena" bridge prompt that opens after a
  // user's first WC pick (see PostFirstPickPromptManager). Users who never
  // engage with WC will not see the Arena walkthrough — that's intentional;
  // WC is the primary surface in the new onboarding.

  // Determine if tab bar should be hidden based on current route
  const shouldHideTabBar = useMemo(() => {
    if (isSquadTvModeActive) return true
    if (worldCupWelcomeOverlayVisible) return true
    // World Cup match stats: full-screen sheet over Picks — hide root tab bar
    if (pathname.includes(WORLDCUP_MATCH_ROUTE_PREFIX)) return true
    // Nested league detail: /app/gameResults/league-detail (first segment is still gameResults)
    if (pathname.includes(LEAGUE_DETAIL_ROUTE)) return true
    if (pathname.includes(MATCH_ROUTE_PREFIX)) return true
    const segments = pathname.split('/').filter(Boolean)
    // Remove 'app' from segments to get the actual route
    const routeSegments = segments.slice(1)
    const currentRoute = routeSegments.length === 0 ? INDEX_ROUTE_KEY : routeSegments[0]
    return (HIDE_TAB_BAR_ROUTES as readonly string[]).includes(currentRoute)
  }, [pathname, worldCupWelcomeOverlayVisible, isSquadTvModeActive])

  // Check if we're on the create page
  const isCreatePage = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    const routeSegments = segments.slice(1)
    const currentRoute = routeSegments.length === 0 ? INDEX_ROUTE_KEY : routeSegments[0]
    return currentRoute === CREATE_ROUTE_KEY
  }, [pathname])

  // The Warmup "Games" shortcut floats on every screen that shows the tab bar, except the
  // Warmup hub itself (no point linking to the screen you're already on).
  const isGamesRoute = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    const routeSegments = segments.slice(1)
    const currentRoute = routeSegments.length === 0 ? INDEX_ROUTE_KEY : routeSegments[0]
    return currentRoute === 'games'
  }, [pathname])

  const layoutStyles = useMemo(() => getLayoutStyles(theme, insets.bottom), [theme, insets.bottom])
  const worldCupTabTitle = useMemo(() => getWcBrand().NAME, [])
  const { total: tabBadgeTotal } = useReceivedInvitesCount()
  const { isGuest } = useGuestGuard()

  const tabBarStyle = useMemo(() => {
    if (shouldHideTabBar) {
      return layoutStyles.tabBarHidden
    }
    return isCreatePage ? layoutStyles.tabBarWithBorder : layoutStyles.tabBar
  }, [shouldHideTabBar, isCreatePage, layoutStyles])

  return (
      <AuthGuard autoDetectRoute>
      <ScreenTracker />
      <PendingGuestMonkeyChallengeFlush />
      <View style={layoutStyles.authenticatedAppRoot}>
        <View style={layoutStyles.tabsContainer}>
          {/* Home is the lobby (specs/home-page AC-2, AC-4b): the first visible tab, and where the
              system back lands from every other tab or tab-hosted screen (Create, Fixtures, a match).
              The default back behaviour ("firstRoute") would send it to the first *declared* tab;
              "initialRoute" ties it to initialRouteName explicitly, so a reorder cannot move the lobby. */}
          <Tabs
            initialRouteName="index"
            backBehavior="initialRoute"
            screenListeners={{
              tabPress: () => {
                void soundService.warmFromUserGesture();
                useSoundStore.getState().playUiTapBrief(SOUND_IDS.UI_TAP_NAV);
              },
            }}
            screenOptions={{
              // Match app chrome so rounded tab bar corners do not reveal the default (white) scene.
              sceneStyle: layoutStyles.sceneContainer,
              tabBarActiveTintColor: theme.primary,
              tabBarInactiveTintColor: theme.gray[500],
              tabBarStyle: tabBarStyle,
              headerShown: false,
              tabBarItemStyle: layoutStyles.tabBarItemStyle,
              tabBarLabelStyle: layoutStyles.tabBarLabelStyle,
            }}
          >
          <Tabs.Screen
            name="index"
            options={{
              title: 'Home',
              tabBarIcon: ({ color, size }) => (
                <Home size={size} color={color} />
              ),
            }}
          />

          <Tabs.Screen
            name="arena"
            options={{
              title: 'Arena',
              tabBarIcon: ({ color, size }) => (
                <Swords size={size} color={color} />
              ),
            }}
          />

          <Tabs.Screen
            name="gameResults"
            options={{
              href: null, // Hidden since 2026-10-07 — Fixtures gave its slot to Home; reached from Home's "See all matches" (AC-11b), league rows, match pages and deep links (specs/home-page AC-2).
              title: 'Fixtures',
            }}
          />

          <Tabs.Screen
            name="friends"
            options={{
              title: 'Dashboard',
              tabBarIcon: ({ color, size }) => (
                <View style={layoutStyles.friendsTabIconWrapper}>
                  <Users size={size} color={color} />
                  {tabBadgeTotal > 0 && (
                    <View style={layoutStyles.friendsTabBadge}>
                      <ZportiBadge
                        count={tabBadgeTotal}
                        variant="notification"
                        size="tab"
                        maxCount={99}
                      />
                    </View>
                  )}
                </View>
              ),
            }}
          />

          <Tabs.Screen
            name="competitions"
            options={{
              title: 'Tournaments',
              tabBarIcon: ({ focused, size }) => (
                <WorldCupTabIcon focused={focused} size={size} locked={isGuest} />
              ),
              tabBarLabelStyle: layoutStyles.worldCupTabBarLabelStyle,
              tabBarActiveTintColor: theme.accent,
              tabBarInactiveTintColor: theme.gray[500],
            }}
          />

          <Tabs.Screen
            name="worldcup"
            options={{
              href: null, // Hidden — reached from the hub's Past section (and deep links); the trophy tab is now the hub.
              title: worldCupTabTitle,
            }}
          />

          <Tabs.Screen
            name="create"
            options={{
              href: null, // Hide from tab bar - accessible via floating button
              title: 'Create Challenge',
            }}
          />

          <Tabs.Screen
            // Full nested path: `competition/` has no `_layout.tsx` of its own (the layout
            // sits at `competition/[competitionId]/_layout.tsx`), so Expo Router flattens the
            // segment and registers the screen under this name — matching `match/[gameId]`
            // and `invite/[token]` below. Naming it `competition` matched nothing and the
            // route leaked into the tab bar as a visible "competition/[competitionId]" tab.
            name="competition/[competitionId]"
            options={{
              href: null, // Hidden — competition screens are reached from the hub, not the tab bar.
              title: 'Competition',
            }}
          />

          <Tabs.Screen
            name="oauth-native-callback"
            options={{
              href: null, // Hide from tab bar - OAuth redirect only
              title: 'OAuth Callback',
            }}
          />

          <Tabs.Screen
            name="oauth-gmail-callback"
            options={{
              href: null, // Hide from tab bar - OAuth redirect only
              title: 'Gmail OAuth Callback',
              headerShown: false, // Hide header completely
            }}
          />

          <Tabs.Screen
            name="tutorial"
            options={{
              href: null, // Hide from tab bar - shown only on first login
              title: 'Tutorial',
            }}
          />

          <Tabs.Screen
            name="onboarding"
            options={{
              href: null, // Hide from tab bar - post-tutorial questionnaire only
              title: 'Onboarding',
            }}
          />

          <Tabs.Screen
            name="privacy-and-data"
            options={{
              href: null,
              title: 'Privacy and data',
            }}
          />

          <Tabs.Screen
            name="account-admin"
            options={{
              href: null,
              title: 'Organization',
            }}
          />

          <Tabs.Screen
            name="impersonate"
            options={{
              href: null,
              title: 'Impersonate link',
            }}
          />

          <Tabs.Screen
            name="admin-impersonate"
            options={{
              href: null,
              title: 'Impersonate (legacy URL)',
            }}
          />

          <Tabs.Screen
            name="unimpersonate"
            options={{
              href: null,
              title: 'Unimpersonate',
            }}
          />

          <Tabs.Screen
            name="legal/[slug]"
            options={{
              href: null,
              title: 'Legal',
            }}
          />

          <Tabs.Screen
            name="invite/[token]"
            options={{
              href: null, // Hide from tab bar - email link only
              title: 'Accept Invitation',
            }}
          />

          <Tabs.Screen
            name="league-detail"
            options={{
              href: null, // Hide from tab bar — opened from Fixtures league row or deep link
              title: 'League Detail',
            }}
          />

          <Tabs.Screen
            name="match/[gameId]"
            options={{
              href: null,
              title: 'Match',
            }}
          />

          <Tabs.Screen
            name="match-details/[gameId]"
            options={{
              href: null,
              title: 'Match Details',
            }}
          />

          <Tabs.Screen
            name="challenge/[id]"
            options={{
              href: null, // Hide from tab bar - navigated from Arena challenge card
              title: 'Challenge Detail',
            }}
          />

          <Tabs.Screen
            name="games"
            options={{
              href: null, // Hide from tab bar — Warmup daily games, opened from an entry card
              title: 'Warmup',
            }}
          />

          <Tabs.Screen
            name="rank-ladder"
            options={{
              href: null, // Hide from tab bar — opened from the header rank badge
              title: 'Rank Ladder',
            }}
          />

          <Tabs.Screen
            name="profile"
            options={{
              href: null, // Hide from tab bar — opened from the header/menu avatar (specs/profile-page)
              title: 'Profile',
            }}
          />

          <Tabs.Screen
            name="[slug]"
            options={{
              href: null, // Hide from tab bar - catch-all route only (must be last)
              title: 'Catch All',
            }}
          />
        </Tabs>
        <FloatingCreateButton />
        {!shouldHideTabBar && !isGamesRoute ? <WarmupFab /> : null}
        <GuestWalkthroughOverlay />
        <FirstChallengeTourOverlay />
        </View>
      </View>
    </AuthGuard>
  )
}

export default function AppLayout() {
  return <AuthenticatedTabs />
}
